import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./GameRoom.scss";
import { Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import Dialog from '../components/ActionDialog';
import AppMenuBar from "../components/AppMenuBar";
import DeskDiscardCards from '../components/DeskDiscardCards';
import DeclareWinButton from '../components/DeclareWinButton';
import GameSettingsFields, { DEFAULT_MONEY_CARDS } from '../components/GameSettingsFields';
import useTurnCountdown from '../hooks/useTurnCountdown';
import ShowedCardsResultRow, { orderMatchPlayers } from '../components/ShowedCardsResultRow';
import RoomConnectionStatus from '../components/RoomConnectionStatus';
import HostJoinRequests from '../components/HostJoinRequests';
import ConfirmDialog from "../components/ConfirmDialog";
import { useNavigate, useParams } from "react-router-dom";
import { clearRoomMembership } from '../services/RoomSession';
import { useRoomSnapshot } from '../hooks/useRoomSnapshot';
import { restoreActiveRoom } from '../services/RoomRestoration';
import { createRoomReader } from '../services/RoomReader';
import { APP_BASE_DOMAIN, GAME_STATE, PLAYER_STATE, STO_PLAYER_ID, STO_PLAYER_NAME, STO_ROOM_ID } from "../constants/commonConstants";
import { LoadingSpinner } from "../components/LoadingSpinner";
import WarningDialog from "../components/WarningDialog";
import { changeName, changeSetting, declareWinner, discard, groupCards, pickDeckCard, pickDiscardedCard, readyGame, requestSwap, responseSwap, showWinningCard, startGame } from "../services/ActionService";
import PokerCard from "../components/PokerCard";
import HalfCard from "../components/HalfCard";
import DraggableCard from "../components/DraggableCard";
import { buildHandRows } from './handLayout';
import CustomDragLayer from "../components/CustomDragLayer";
import RoomInfoDialog from "../components/RoomInfoDialog";
import LowInternet from "../components/LowInternet";
import CardShuffleLoading from '../components/CardShuffleLoading';
import ExpandableText from '../components/ExpandableText';
import ChatDialog from "../components/ChatDialog";
import { sendMessage } from "../services/ChatService";
import RoomVoiceChat from "../components/RoomVoiceChat";
import { customStorage, isNullOrEmptyString } from "../services/CommonService";
import SignalRService from "../services/SignalRService";
import { useRoomExit } from "../hooks/useRoomExit";
import { useRoomPlayerRemoval } from '../hooks/useRoomPlayerRemoval';
import { createGameAudioPlayer } from '../services/GameAudioPlayer';
import { gameActionSounds } from '../services/GameActionSounds';
import { autoArrangeCards } from '../services/AutoArrangeCards';
import SortIcon from '@mui/icons-material/Sort';
import { createChatNotifications } from '../services/ChatNotifications';
import { useToast } from "../components/Toast";
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import CloseIcon from '@mui/icons-material/Close';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/Check';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';

import discardCardSound from "../assets/game-sound/discard-card.mp3";
import gameStartSound from "../assets/game-sound/game-start.mp3";
import joinLeaveSound from "../assets/game-sound/join-leave-alert.mp3";
import pickCardSound from "../assets/game-sound/pick-card.mp3";
import shuffleSound from "../assets/game-sound/riffle-card-shuffle.mp3";
import showWinningSound from "../assets/game-sound/show-winning-cards.mp3";
import winDeclareSound from "../assets/game-sound/win-declare.mp3";
import readyClickSound from "../assets/game-sound/ready-click.mp3";
import FeedbackDialog from '../components/FeedbackDialog';
import { useSubscription } from '../context/SubscriptionContext';
import SubscriptionDialog from '../components/SubscriptionDialog';
import SubscriptionWarning from '../components/SubscriptionWarning';
import { DEFAULT_CARD_BACK_DESIGN, getCardBackImage, preloadCardBackImage } from '../services/CardBackPreference';
import PlayerAvatar from '../components/PlayerAvatar';

const gameAudios = {
  discardCardSound: discardCardSound,
  gameStartSound: gameStartSound,
  joinLeaveSound: joinLeaveSound,
  pickCardSound: pickCardSound,
  shuffleSound: shuffleSound,
  showWinningSound: showWinningSound,
  winDeclareSound: winDeclareSound,
  readyClickSound: readyClickSound
}

const OPENING_PHASE = Object.freeze({ idle: 'idle', shuffling: 'shuffling', dealing: 'dealing' });



const GameRoom = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { roomId, playerId } = useParams();
  const { feature: subscriptionFeature } = useSubscription();
  const soundAccess = subscriptionFeature('poker', 'sounds');
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const soundAllowedRef = useRef(soundAccess.allowed);
  soundAllowedRef.current = soundAccess.allowed;
  const navigate = useNavigate();
  const roomReaderRef = useRef(null);
  const [connectionReady, setConnectionReady] = useState(false);
  const [connectionState, setConnectionState] = useState({ ready: false, status: 'initializing', exhausted: false });
  useEffect(() => {
    const changed = event => {
      if (!event.detail.ready) roomReaderRef.current?.cancel();
      setConnectionReady(event.detail.ready); setConnectionState(event.detail);
    };
    window.addEventListener('pwf-connection-state', changed);
    return () => window.removeEventListener('pwf-connection-state', changed);
  }, []);
  useEffect(() => {
    // Presence must not depend on the normal poll countdown: frequent hub
    // updates reset that countdown even for a player who has not taken a turn.
    const heartbeat = setInterval(() => {
      roomReaderRef.current?.read();
    }, 30000);
    return () => clearInterval(heartbeat);
  }, [roomId, playerId]);
  useEffect(() => {
    const membershipEnded = (event) => {
      if (event.detail?.roomId !== roomId) return;
      if (Date.parse(event.detail?.autoRejoinUntilUtc || '') > Date.now()) {
        showToast({ message: t('roomMembership.autoRejoining'), variant: 'info' });
        SignalRService.retry();
        return;
      }
      clearRoomMembership(roomId);
      if (customStorage.getItem(STO_ROOM_ID) === roomId) {
        customStorage.removeItem(STO_ROOM_ID);
        customStorage.removeItem(STO_PLAYER_ID);
      }
      SignalRService.setRequired(false);
      SignalRService.leaveRoom(roomId, playerId).catch(() => {});
      setRoomData(null);
      setSelectedCardId([]);
      showToast({ message: t(event.detail?.code === 'ROOM_NOT_FOUND' ? 'roomMembership.roomMissingHelp' : 'roomMembership.membershipEnded'), variant: 'info' });
      navigate('/poker', { replace: true });
    };
    window.addEventListener('pwf-room-membership-ended', membershipEnded);
    return () => window.removeEventListener('pwf-room-membership-ended', membershipEnded);
  }, [roomId, playerId, navigate, showToast, t]);
  const [loading, setLoading] = useState(false);
  const actionInFlightRef = useRef(false);
  const [confirmText, setConfirmText] = useState('');
  const { roomData, setRoomData, currentDiscardedCard } = useRoomSnapshot(roomId, playerId);
  const turnSeconds = useTurnCountdown(roomData?.turnDeadlineUtc, roomData?.serverTimeUtc);
  const turnExpired = turnSeconds === 0 || roomData?.players?.find(player => player.playerId === playerId)?.turnTimedOut;
  const isRandomRoom = roomData?.roomType === 1 || !/^\d{4,12}$/.test(roomId || '');
  const [playingPlayers, setPlayingPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [ownCards, setOwnCards] = useState([]);
  const [lowerShowedCard, setLowerShowedCard] = useState(null);
  const [openingPhase, setOpeningPhase] = useState(OPENING_PHASE.idle);
  const [cardBackReady, setCardBackReady] = useState(false);
  const isOpeningShuffle = openingPhase === OPENING_PHASE.shuffling;
  const isOpeningDeal = openingPhase === OPENING_PHASE.dealing;
  const isOpeningSequence = openingPhase !== OPENING_PHASE.idle;
  const openingShuffleSequence = useRef(0);
  const [gameState, setGameState] = useState(null);
  const canSwapPositions = roomData?.gameState === GAME_STATE.initialHosted || roomData?.gameState === GAME_STATE.readyForNextMatch;
  // const [playerState, setPlayerState] = useState(null);
  // const [playerId, setPlayerId] = useState(null);
  const playerIndex = Math.max(0, roomData?.players?.findIndex(p => p.playerId === playerId) ?? 0);
  const [activePlayerIndex, setActivePlayerIndex] = useState(null);
  const [playerInfo, setPlayerInfo] = useState(null);
  const [activePlayer, setActivePlayer] = useState(null);
  const [prevPlayer, setPrevPlayer] = useState(null);
  const [, setNextPlayerIndex] = useState(null);
  const [nextPlayer, setNextPlayer] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [hostPlayer, setHostPlayer] = useState(null);
  const [gameMessage, setGameMessage] = useState("");
  const [triggerUpdateRoom, setTriggerUpdateRoom] = useState(false);
  // const [winnginPlayerName, setWinningPlayerName] = useState(null);
  const [isPickedDeskCardMove, setIsPickedDeskCardMove] = useState(false);
  const [isPickedDiscardedCardMove, setIsPickedDiscardedCardMove] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState([]);
  const [isFirstTurn, setIsFirstTurn] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const errorMessageRef = useRef('');
  const loadingSituationRef = useRef('');
  const [, setSpectatingPlayers] = useState([]);
  const [deckCardCount, setDeckCardCount] = useState(0);
  const [showPickPairCardConfirm, setShowPickPairCardConfirm] = useState(false);
  const [showConfirmDeclare, setShowConfirmDeclare] = useState(false);
  const [jokerDiscardCardId, setJokerDiscardCardId] = useState(null);
  const [showGameInfoDialog, setShowGameInfoDialog] = useState(false);
  const [removePlayer, setRemovePlayer] = useState(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [groupedCards, setGroupedCards] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [initialDelayComplete, setInitialDelayComplete] = useState(false);
  const containerRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [message, setMessage] = useState('');
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [chatInfos, setChatInfos] = useState([]);
  const [playersForChat, setPlayersForChat] = useState([]);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const chatNotifications = useRef(createChatNotifications());
  useEffect(() => {
    chatNotifications.current.reset();
    setNewMessageCount(0);
  }, [roomId, playerId]);
  const pollingSeconds = 12;
  const [secondsToPolling, setSecondsToPolling] = useState(pollingSeconds);
  const audioRef = useRef(null);
  const hasUserInteracted = useRef(false);
  const [isSoundOn, setIsSoundOn] = useState(false);
  const soundOnRef = useRef(false);
  soundOnRef.current = isSoundOn;
  const soundSnapshotRef = useRef(null);
  const [showAutoArrangeConfirm, setShowAutoArrangeConfirm] = useState(false);
  const [showSettingDialog, setShowSettingDialog] = useState(false);
  const [settingDeckCount, setSettingDeckCount] = useState("");
  const [settingJokerPairs, setSettingJokerPairs] = useState("");
  const [settingCardsPerPlayer, setSettingCardsPerPlayer] = useState(13);
  const [settingMoneyCards, setSettingMoneyCards] = useState({ ...DEFAULT_MONEY_CARDS });
  const [settingMaxWaitSeconds, setSettingMaxWaitSeconds] = useState(null);
  const [settingCardBackDesign, setSettingCardBackDesign] = useState(DEFAULT_CARD_BACK_DESIGN);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [showPlayerNameDialog, setShowPlayerNameDialog] = useState(false);
  const [tempPlayerName, setTempPlayerName] = useState("");

  const swapRequestsRef = useRef([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [showSwapPositionsDialog, setShowSwapPositionsDialog] = useState(false);

  const maxPlayerNameLength = 8;

  const isSpectating = (playerState) => {
    const isNotPlaying = [
      PLAYER_STATE.leftRoom,
      PLAYER_STATE.dropped,
      PLAYER_STATE.spectating,
    ].includes(playerState);
    return isNotPlaying;
  };

  const isPlayingGame = (playPlayers, index) => {
    const playerState = playPlayers[index]?.playerState;
    const isPlaying = [
      PLAYER_STATE.turnPick,
      PLAYER_STATE.pickedDeckCard,
      PLAYER_STATE.pickedDiscardedCard,
      PLAYER_STATE.waitingTurn,
      PLAYER_STATE.declaredWin,
      PLAYER_STATE.waitWinCardsShow
    ].includes(playerState);
    return isPlaying;
  };

  const errorsAndWarnsMsg = {
    noInternet: t('gameRoom.errors.noInternet'),
    invitePlayers: t('gameRoom.errors.invitePlayers'),
    noCardToPickFromDeck: t('gameRoom.errors.noCardToPickFromDeck'),
    noCardToPickFromDiscardedCard: t('gameRoom.errors.noCardToPickFromDiscardedCard'),
    selectCardToDiscard: t('gameRoom.errors.selectCardToDiscard'),
    selectOneOrPairCard: t('gameRoom.errors.selectOneOrPairCard'),
    selectCardToDeclare: t('gameRoom.errors.selectCardToDeclare'),
    cannotDiscardPickedCard: t('gameRoom.errors.cannotDiscardPickedCard'),
    cannotDiscardNextPlayerPickCard: t('gameRoom.errors.cannotDiscardNextPlayerPickCard'),
    selectMoreThanOneCardsToGroup: t('gameRoom.errors.selectMoreThanOneCardsToGroup'),
    cannotChangeSetting: t('gameRoom.errors.cannotChangeSetting'),
    nameAlreadyExist: t('gameRoom.errors.nameAlreadyExist'),
    roomNotFound: t('gameRoom.errors.roomNotFound'),
    cannotSwapWilePlaying: t('gameRoom.errors.cannotSwapWhilePlaying'),
  }



  // Store i18n keys (+params) so the displayed message is translated at render
  // time and always reflects the current language (no stale value on toggle).
  const msgInfos = {
    startTheGame: { key: 'gameRoom.info.startTheGame' },
    waitHostStart: { key: 'gameRoom.info.waitHostStart' },
    clickReady: { key: 'gameRoom.info.clickReady' },
    turnToPick: { key: 'gameRoom.info.turnToPick' },
    turnToDiscardOrDeclareWin: {
      key: playerInfo?.pickedCards?.length >= 4
        ? 'gameRoom.info.turnToDiscardMaxPicked'
        : playerInfo?.mustDeclareWinner
          ? 'gameRoom.info.turnToDiscardMustDeclare'
          : 'gameRoom.info.turnToDiscardOrDeclareWin',
    },
    waitOtherPlayerTurn: {
      key: 'gameRoom.info.waitOtherPlayerTurn',
      params: {
        playerName: activePlayer?.playerName,
        turnAction: activePlayer?.playerState === PLAYER_STATE.turnPick
          ? t('gameRoom.info.turnActionPick')
          : (activePlayer?.playerState === PLAYER_STATE.pickedDeckCard || activePlayer?.playerState === PLAYER_STATE.pickedDiscardedCard)
            ? t('gameRoom.info.turnActionDiscard')
            : '',
      },
    },
    showWinCard: { key: 'gameRoom.info.showWinCard' },
    waitShowWinCard: { key: 'gameRoom.info.waitShowWinCard', params: { playerName: activePlayer?.playerName } },
    waitOtherPlayerReady: { key: 'gameRoom.info.waitOtherPlayerReady' },
    waitForNextMatch: { key: 'gameRoom.info.waitForNextMatch' },
  }

  const fetchRoomData = async () => {
    return roomReaderRef.current?.read();
  };

  const resetPollingCount = () => {
    setSecondsToPolling(pollingSeconds);
  }

  useEffect(() => {
    const reader = createRoomReader({
      roomId, playerId,
      canRead: () => Boolean(roomId && playerId) && SignalRService.isReady() && document.visibilityState !== 'hidden',
      onStart: () => setSecondsToPolling(pollingSeconds),
      onSnapshot: setRoomData,
      onError: error => {
        const status = error?.response?.status;
        if (!status || status === 408 || status === 429 || status >= 500) {
          // An outage is not a room error or logout. The coordinator pauses
          // ordinary polling until authority/state/subscription recover.
          SignalRService.requestRecovery();
        } else if (status === 400) setErrorMessage(error?.response?.data?.message);
      },
    });
    roomReaderRef.current = reader;
    const hidden = () => { if (document.visibilityState === 'hidden') reader.cancel(); };
    const pageHidden = () => reader.cancel();
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', pageHidden);
    return () => {
      reader.dispose();
      if (roomReaderRef.current === reader) roomReaderRef.current = null;
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', pageHidden);
    };
  }, [roomId, playerId, setRoomData]);

  useEffect(() => {
    const openingSequenceTracker = openingShuffleSequence;
    let disposed = false;
    setIsFirstLoad(true);
    setInitialDelayComplete(false);
    // The global connection dialog is relevant only while gameplay requires
    // the room hub. Initializing the socket is not itself a connection loss.
    SignalRService.setRequired(true);
    audioRef.current = createGameAudioPlayer(Object.values(gameAudios));
    audioRef.current.preload();

    const enableAudioContext = () => {
      hasUserInteracted.current = true; // Mark as interacted
      audioRef.current?.unlock()?.catch(() => { }); // Browser gesture unlocks Web Audio.
    };

    window.addEventListener("click", enableAudioContext);
    window.addEventListener("touchend", enableAudioContext);
    window.addEventListener("keydown", enableAudioContext);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const firstLoadTimer = setTimeout(() => {
      setInitialDelayComplete(true);
    }, 2000);

    const signalRInitialize = async () => {
      try {
        SignalRService.onJoin = async (roomData) => {
          try {
            handlePlayAudio(gameAudios.joinLeaveSound);
            setRoomData(roomData);
            resetPollingCount();
          } catch (error) {
            console.error('receive sdp offer error', error);
          }
        };

        SignalRService.onLeave = async (roomData) => {
          try {
            handlePlayAudio(gameAudios.joinLeaveSound);
            setRoomData(roomData);
            resetPollingCount();
          } catch (error) {
            console.error('receive sdp offer error', error);
          }
        };

        SignalRService.onStateChange = async (roomData) => {
          try {
            setRoomData(roomData);
            resetPollingCount();
          } catch (error) {
            console.error('receive sdp offer error', error);
          }
        };

        SignalRService.onPlayerJoin = (player) => {
          try {
            if (player?.playerName) {
              showToast({
                message: t('gameRoom.toasts.playerJoined', { playerName: player.playerName }),
                variant: 'success',
              });
            }
          } catch (error) {
            console.error('join toast error', error);
          }
        };

        SignalRService.onPlayerLeave = (player) => {
          try {
            if (player?.playerName) {
              showToast({
                message: t('gameRoom.toasts.playerLeft', { playerName: player.playerName }),
                variant: 'info',
              });
            }
          } catch (error) {
            console.error('leave toast error', error);
          }
        };

        SignalRService.onSendMessage = async (chatInfos) => {
          try {
            setChatInfos(chatInfos);
          } catch (error) {
            console.error('receive sdp offer error', error);
          }
        };
        SignalRService.configureRoomRecovery(roomId, playerId, async signal => {
          const snapshot = await restoreActiveRoom(roomId, playerId, () => !disposed && SignalRService.isRequired(), signal);
          if (disposed || !snapshot) return false;
          setRoomData(snapshot);
          setErrorMessage('');
          resetPollingCount();
          return true;
        });
        await SignalRService.start();
      } catch (error) {
        // The visible-time coordinator owns retries; an outage is not Leave.
      }
    };

    signalRInitialize();

    const pollingInterval = setInterval(() => {
      if (document.visibilityState !== 'hidden' && SignalRService.isReady() && isNullOrEmptyString(errorMessageRef.current) && isNullOrEmptyString(loadingSituationRef.current)) {
        setSecondsToPolling(prev => Math.max(0, prev - 1));
      }
    }, 1000);

    return () => {
      disposed = true;
      openingSequenceTracker.current++;
      clearTimeout(firstLoadTimer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('click', enableAudioContext);
      window.removeEventListener('touchend', enableAudioContext);
      window.removeEventListener('keydown', enableAudioContext);
      clearInterval(pollingInterval);
      audioRef.current?.dispose();
      audioRef.current = null;
      SignalRService.onJoin = null;
      SignalRService.onLeave = null;
      SignalRService.onStateChange = null;
      SignalRService.onPlayerJoin = null;
      SignalRService.onPlayerLeave = null;
      SignalRService.onSendMessage = null;
      SignalRService.setRequired(false);
    };
  }, [roomId, playerId]);

  useEffect(() => {
    if (initialDelayComplete && roomData?.roomId === roomId) setIsFirstLoad(false);
  }, [initialDelayComplete, roomData, roomId]);

  const handlePlayAudio = (audioSrc, waitForEnd = false) => {
    if (!audioSrc || !audioRef.current || !hasUserInteracted.current || !soundOnRef.current || !soundAllowedRef.current) return Promise.resolve(false);

    const playback = waitForEnd ? audioRef.current.playUntilEnded(audioSrc) : audioRef.current.play(audioSrc);
    return playback.then(() => true).catch((err) => { console.error("Audio playback error:", err); return false; });
  };

  const handlePlayLoopAudio = (audioSrc) => {
    if (!audioSrc || !audioRef.current || !hasUserInteracted.current || !soundOnRef.current || !soundAllowedRef.current) return Promise.resolve(false);
    return audioRef.current.playLoop(audioSrc).then(() => true).catch(() => false);
  };

  useEffect(() => {
    if (!isSoundOn || !soundAccess.allowed) audioRef.current?.stop();
  }, [isSoundOn, soundAccess.allowed]);

  useEffect(() => {
    let active = true;
    setCardBackReady(false);
    preloadCardBackImage(roomData?.cardBackDesign).then(() => {
      if (active) setCardBackReady(true);
    });
    return () => { active = false; };
  }, [roomData?.cardBackDesign]);

  useEffect(() => {
    const previous = soundSnapshotRef.current;
    const sounds = gameActionSounds(previous, roomData);
    soundSnapshotRef.current = roomData;
    const matchStarted = previous?.roomId === roomData?.roomId &&
      previous?.gameState !== GAME_STATE.playing && roomData?.gameState === GAME_STATE.playing;
    if (!matchStarted) {
      sounds.forEach(sound => handlePlayAudio(gameAudios[sound]));
      return;
    }

    const sequence = ++openingShuffleSequence.current;
    setOpeningPhase(OPENING_PHASE.shuffling);
    const minimumAnimation = new Promise(resolve => setTimeout(resolve, 1600));
    const cardBackReady = preloadCardBackImage(roomData?.cardBackDesign);
    if (sounds.includes('shuffleSound')) void handlePlayLoopAudio(gameAudios.shuffleSound);
    const openingSounds = sounds.filter(sound => sound !== 'shuffleSound').map(sound => handlePlayAudio(gameAudios[sound], true));
    Promise.allSettled([minimumAnimation, cardBackReady, ...openingSounds])
      .then(() => {
        if (openingShuffleSequence.current !== sequence) return;
        setOpeningPhase(OPENING_PHASE.dealing);
        const openingDealDuration = ((roomData?.cardsPerPlayer || 13) - 1) * 115 +
          Math.max(0, (roomData?.players?.length || 1) - 1) * 25 + 780;
        setTimeout(() => {
          if (openingShuffleSequence.current === sequence) {
            setOpeningPhase(OPENING_PHASE.idle);
            // The shuffle track belongs to the whole opening sequence. Stop it
            // only after the final deal animation has completed.
            audioRef.current?.stop();
          }
        }, openingDealDuration);
      });
  }, [roomData]);

  useEffect(() => {
    if (secondsToPolling <= 0 && !loading && isNullOrEmptyString(errorMessage)) {
      fetchRoomData();
    }
  }, [secondsToPolling]);


  useEffect(() => {
    errorMessageRef.current = errorMessage;
  }, [errorMessage]);
  useEffect(() => {
    loadingSituationRef.current = loading ? 'Loading' : '';
  }, [loading])

  useEffect(() => {
    const allPlrs = roomData?.players || [];
    const spectators = allPlrs.filter(p => p.playerState === PLAYER_STATE.spectating);
    let playingPlrs = allPlrs?.filter(p => !isSpectating(p.playerState)) || [];
    playingPlrs = playingPlrs ? [...playingPlrs?.slice(playerIndex), ...playingPlrs?.slice(0, playerIndex)] : [];
    // const playingPlrs = roomData?.players?.filter(p => p.playerState !== PLAYER_STATE.spectating);
    const reArrangedPlayers = allPlrs ? [...allPlrs?.slice(playerIndex), ...allPlrs?.slice(0, playerIndex)] : [];
    const pInfo = allPlrs?.find(p => p.playerId === playerId);
    const activePlr = reArrangedPlayers?.find(p => p.playerState === PLAYER_STATE.turnPick || p.playerState === PLAYER_STATE.pickedDeckCard
      || p.playerState === PLAYER_STATE.pickedDiscardedCard || p.playerState === PLAYER_STATE.declaredWin);
    const activePlrIndex = reArrangedPlayers?.findIndex(p => p.playerId === activePlr?.playerId);
    const gState = roomData?.gameState;
    const ownPcards = roomData?.ownCards;
    setAllPlayers(reArrangedPlayers);
    setSpectatingPlayers(spectators);
    setActivePlayerIndex(activePlrIndex);
    setActivePlayer(activePlr);
    const hostPlr = allPlrs.find(p => p.playerId === roomData?.hostPlayerId) || allPlrs.find(p => p.isJoined !== false);
    setHostPlayer(hostPlr)
    setIsHost(hostPlr?.playerId === playerId);
    setPlayingPlayers(playingPlrs);
    setPlayerInfo(pInfo);
    setOwnCards(prev => (reorderObjectArray(prev, ownPcards, 'cardId')));
    setLowerShowedCard(roomData?.lowerShowedCard);
    setGameState(gState);
    if (activePlrIndex === -1) {
      setNextPlayerIndex(-1);
      setPrevPlayer(null);
      setNextPlayer(null);
    } else {
      const prevPlrIndex = playingPlrs
        .map((_, i) => (activePlrIndex - i - 1 + reArrangedPlayers.length) % reArrangedPlayers.length)
        .find(index => isPlayingGame(reArrangedPlayers, index));

      const nextPlrIndex = reArrangedPlayers
        .map((_, i) => (activePlrIndex + i + 1) % reArrangedPlayers.length)
        .find(index => isPlayingGame(reArrangedPlayers, index));
      setNextPlayerIndex(nextPlrIndex);
      setPrevPlayer(reArrangedPlayers[prevPlrIndex]);
      setNextPlayer(reArrangedPlayers[nextPlrIndex]);
    }
    setIsFirstTurn(roomData?.isFirstTurn);
    setDeckCardCount(roomData?.deckCardCount);
    if (roomData?.roomId === roomId) chatNotifications.current.initialize(roomData.chatInfos || []);
    setChatInfos(roomData?.chatInfos || []);
    const plrsForChat = allPlrs?.map(p => ({
      playerId: p.playerId,
      playerName: p.playerName,
      plyerState: p.playerState
    }));
    setPlayersForChat(plrsForChat);
    // const swapReqs = roomData?.swapReqs?.map(sr=> {
    //   return {
    //     ...sr,

    //   }
    // })
    const relatedSwapRequests = roomData?.swapRequests?.filter(sr => sr.requestPlayerId === playerId || sr.targetPlayerId === playerId);
    setSwapRequests(relatedSwapRequests || []);


    setTriggerUpdateRoom(prev => !prev);
  }, [roomData]);

  useEffect(() => {
    const playerState = playerInfo?.playerState;
    const getMessageInfo = () => {
      if (gameState === GAME_STATE.initialHosted) {
        if (isHost) {
          return msgInfos.startTheGame;
        } else {
          return msgInfos.waitHostStart;
        }
      }
      if (gameState === GAME_STATE.playing) {
        if (playerState === PLAYER_STATE.turnPick) {
          return msgInfos.turnToPick;
        }
        if (playerState === PLAYER_STATE.pickedDeckCard || playerState === PLAYER_STATE.pickedDiscardedCard) {
          return msgInfos.turnToDiscardOrDeclareWin;
        }
        if (playerState === PLAYER_STATE.waitingTurn) {
          return msgInfos.waitOtherPlayerTurn;
        }
        if (isSpectating(playerState)) {
          return msgInfos.waitForNextMatch;
        }
      }
      if (gameState === GAME_STATE.winDeclared) {
        if (playerState === PLAYER_STATE.declaredWin) {
          return msgInfos.showWinCard;
        }
        if (playerState === PLAYER_STATE.waitWinCardsShow) {
          return msgInfos.waitShowWinCard;
        }
      }
      if (gameState === GAME_STATE.readyForNextMatch) {
        if (playerState === PLAYER_STATE.notReady) {
          return msgInfos.clickReady;
        }
        if (playerState === PLAYER_STATE.ready) {
          return msgInfos.waitOtherPlayerReady;
        }
      }
    }
    setGameMessage(getMessageInfo());
    movePickedDeskCard();
    movePickedDiscardedCard();

  }, [triggerUpdateRoom]);

  useEffect(() => {
    const oldToResponseRequests = swapRequestsRef.current?.filter(sr => sr.targetPlayerId === playerId) || [];
    const newToResponseRequests = swapRequests?.filter(sr => sr.targetPlayerId === playerId) || [];
    if (!canSwapPositions) setShowSwapPositionsDialog(false);
    else if (newToResponseRequests?.length > oldToResponseRequests?.length) {
      setShowSwapPositionsDialog(true);
    }
    swapRequestsRef.current = swapRequests;
  }, [swapRequests, canSwapPositions, playerId]);

  const movePickedDeskCard = () => {
    if (activePlayer?.playerState === PLAYER_STATE.pickedDeckCard) {
      setIsPickedDeskCardMove(true);
    } else {
      setIsPickedDeskCardMove(false);
    }
  }
  const movePickedDiscardedCard = () => {
    if (activePlayer?.playerState === PLAYER_STATE.pickedDiscardedCard) {
      setIsPickedDiscardedCardMove(true);
    } else {
      setIsPickedDiscardedCardMove(false);
    }
  }


  // Show a single "match restarting" toast when the table transitions into the
  // ready-up state after a player has left / dropped mid-match. The ref ensures
  // it fires only once per transition, not on every subsequent room update.
  const prevGameStateRef = useRef(null);
  useEffect(() => {
    const prevState = prevGameStateRef.current;
    prevGameStateRef.current = gameState;
    if (gameState === GAME_STATE.readyForNextMatch &&
        prevState !== null &&
        prevState !== GAME_STATE.readyForNextMatch) {
      showToast({
        message: t('gameRoom.toasts.matchRestarting'),
        variant: 'warning',
      });
    }
  }, [gameState]);


  const reorderObjectArray = (oldArray, newArray, key) => {

    oldArray = oldArray ?? [];
    newArray = newArray ?? [];

    // Preserve the order of elements in the original array that are present in the new array
    const preservedOrder = oldArray?.filter(originalItem =>
      newArray.some(newItem => newItem[key] === originalItem[key])
    );

    // Add new elements that are in the new array but not in the original array
    const addedItems = newArray?.filter(newItem =>
      !preservedOrder.some(preservedItem => preservedItem[key] === newItem[key])
    );

    // Merge the arrays
    return [...preservedOrder, ...addedItems];
  };

  const moveCard = useCallback(async (dragCardId, hoverCardId) => {
    setOwnCards((prev) => {
      const updatedCards = [...prev];
      const actualDragIndex = updatedCards.findIndex(c => c.cardId === dragCardId);
      const actualHoverIndex = updatedCards.findIndex(c => c.cardId === hoverCardId);

      if (actualDragIndex === -1 || actualHoverIndex === -1) {
        return prev;
      }

      const [draggedCard] = updatedCards.splice(actualDragIndex, 1);

      updatedCards.splice(actualHoverIndex, 0, draggedCard);
      const sortedUpdatedGroup = updatedCards?.sort((a, b) => b.group - a.group);

      return sortedUpdatedGroup;
    });
  }, []);

  useEffect(() => {
    const newGroupedCards = ownCards.reduce((acc, card) => {
      if (!acc[card?.group]) acc[card?.group] = [];
      acc[card?.group].push(card);
      return acc;
    }, {});
    let sortedGroup = Object.values(newGroupedCards)?.sort((a, b) => b[0]?.group - a[0]?.group);
    sortedGroup = [...sortedGroup, []]; // To smooth dragging for last group card!
    setGroupedCards(sortedGroup);
  }, [ownCards]);

  // Seat coordinates are CSS custom properties so responsive table layouts and
  // the card-movement animations always use the same destination.
  const playerPositions = [
    { top: 'var(--seat-0-top)', left: 'var(--seat-0-left)', slot: 0 },
    { top: 'var(--seat-1-top)', left: 'var(--seat-1-left)', slot: 1 },
    { top: 'var(--seat-2-top)', left: 'var(--seat-2-left)', slot: 2 },
    { top: 'var(--seat-3-top)', left: 'var(--seat-3-left)', slot: 3 },
    { top: 'var(--seat-4-top)', left: 'var(--seat-4-left)', slot: 4 },
    { top: 'var(--seat-5-top)', left: 'var(--seat-5-left)', slot: 5 }
  ];

  function getAllPositionedPlayers(playerCount) {
    const predefinedPatterns = [
      [0],
      [0, 3],
      [0, 2, 4],
      [0, 2, 3, 4],
      [0, 1, 2, 4, 5],
      [0, 1, 2, 3, 4, 5],
    ];

    if (playerCount <= 0 || playerCount > predefinedPatterns?.length) {
      return [];
    }

    return predefinedPatterns[playerCount - 1]?.map(index => playerPositions[index]);
  }

  const renderPickedCard = (pickedCards) => {

    return (
      pickedCards.map((card, index) => {
        // const angle = (index - (numCards - 1) / 2) * 15;
        // const translateX = (index - (numCards - 1) / 2) * 20;

        // const angle = (index - (numCards - 1) / 2) * -15;
        // const translateX = (numCards - 1 - index - (numCards - 1) / 2) * 20;

        // Progress cards from left to right and stack later cards above earlier
        // ones. The exposed left edge always contains the rank and suit.
        return (
          <PokerCard key={card.cardId} card={card} wrapperClass="card picked-card" isShowType={false} />
        );
      })
    );
  };

  const handleHorizontalCardScroll = (event) => {
    const container = event.currentTarget;
    if (container.scrollWidth <= container.clientWidth) return;

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      container.scrollLeft += event.deltaY;
      event.preventDefault();
    }
  };

  const beginGameAction = () => {
    if (actionInFlightRef.current) return false;
    actionInFlightRef.current = true;
    return true;
  };
  const endGameAction = () => { actionInFlightRef.current = false; };

  const isPlayerTurn = (pInfo) => {
    return (gameState === GAME_STATE.playing &&
      (pInfo?.playerState === PLAYER_STATE.turnPick || pInfo?.playerState === PLAYER_STATE.pickedDeckCard || pInfo?.playerState === PLAYER_STATE.pickedDiscardedCard)) ||
      (gameState === GAME_STATE.winDeclared && pInfo.playerState === PLAYER_STATE.declaredWin)
  }

  // RoomActionFilter broadcasts successful HTTP mutations. These handlers
  // apply the response locally; a second hub notification can race a reconnect.
  const handlePickDeckCard = async () => {
    if (!SignalRService.isReady() || turnExpired || actionInFlightRef.current) return;
    if (gameState !== GAME_STATE.playing || playerInfo?.playerState !== PLAYER_STATE.turnPick) return;

    if (deckCardCount <= 0) {
      setErrorMessage(errorsAndWarnsMsg.noCardToPickFromDeck);
      return;
    }
    if (!beginGameAction()) return;

    try {
      const response = await pickDeckCard(roomId, playerId);
      if (response) {
        setRoomData(response);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err.status === 400) {
        setErrorMessage(err?.response?.data?.message)
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      setShowConfirmDeclare(false);
      setShowPickPairCardConfirm(false)
      endGameAction();
    }
  }

  const handlePickDiscardedCard = async () => {
    if (!SignalRService.isReady() || turnExpired || actionInFlightRef.current) return;
    if (gameState !== GAME_STATE.playing || playerInfo?.playerState !== PLAYER_STATE.turnPick) return;

    if (!currentDiscardedCard) {
      setErrorMessage(errorsAndWarnsMsg.noCardToPickFromDiscardedCard);
      return;
    }

    if (roomData.isDiscardedPairCards) {
      setShowPickPairCardConfirm(true);
      return;
    }

    if (playerInfo?.pickedCards?.length >= 3) {
      setShowConfirmDeclare(true);
      return;
    }

    await executeDiscardedCardPick();
  }

  const executeDiscardedCardPick = async (mustDeclareWinner = false) => {
    if (!SignalRService.isReady() || turnExpired || !beginGameAction()) return;
    try {
      const response = await pickDiscardedCard(roomId, playerId, mustDeclareWinner);
      if (response) {
        setRoomData(response);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err.status === 400) {
        setErrorMessage(err?.response?.data?.message)
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      setShowConfirmDeclare(false);
      setShowPickPairCardConfirm(false);
      endGameAction();
    }
  }

  const handleCardSelect = (cardId) => {
    setSelectedCardId(prev =>
      prev.includes(cardId)
        ? prev.filter(cId => cId !== cardId) // Remove cardId if already selected
        : [...prev, cardId] // Add cardId if not selected
    );
  }

  const handleDiscardClick = async () => {
    if (!SignalRService.isReady() || turnExpired || actionInFlightRef.current) return;
    const toDiscardCard = ownCards?.find(c => c.cardId === selectedCardId[0]);

    if (!toDiscardCard || gameState !== GAME_STATE.playing || (playerInfo?.playerState !== PLAYER_STATE.pickedDeckCard && playerInfo?.playerState !== PLAYER_STATE.pickedDiscardedCard)) {
      setErrorMessage(errorsAndWarnsMsg.selectCardToDiscard);
      return;
    }
    if (!selectedCardId || selectedCardId.length === 0) {
      setErrorMessage(errorsAndWarnsMsg.selectCardToDiscard);
      return;
    }

    if (playerInfo?.pickedCards?.find(c => c.cardId === selectedCardId[0])) {
      setErrorMessage(errorsAndWarnsMsg.cannotDiscardPickedCard);
      return;
    }

    if (selectedCardId.some(id => ownCards.find(card => card.cardId === id)?.type === 'jokers')) {
      if (selectedCardId.length !== 1) {
        setErrorMessage(t('gameRoom.errors.singleJokerOnly'));
        return;
      }
      setJokerDiscardCardId(toDiscardCard.cardId);
      return;
    }

    const pairCardToDiscardNextTurn = ownCards?.find(c => c.cardId === selectedCardId[1]);
    if (pairCardToDiscardNextTurn) {
      if (selectedCardId.length !== 2 || toDiscardCard.rank !== pairCardToDiscardNextTurn.rank || toDiscardCard.type !== pairCardToDiscardNextTurn.type) {
        setErrorMessage(errorsAndWarnsMsg.selectOneOrPairCard);
        return;
      }

      if (playerInfo?.pickedCards?.find(c => c.cardId === selectedCardId[1])) {
        setErrorMessage(errorsAndWarnsMsg.cannotDiscardPickedCard);
        return;
      }
    } else {
      if (selectedCardId.length !== 1) {
        setErrorMessage(errorsAndWarnsMsg.selectOneOrPairCard);
        return;
      }
    }

    if (nextPlayer?.pickedCards?.find(c => c.rank === toDiscardCard.rank) && !(nextPlayer?.discardedCards?.find(c => c.rank === toDiscardCard.rank))) {
      setErrorMessage(errorsAndWarnsMsg.cannotDiscardNextPlayerPickCard);
      return;
    }

    if (!beginGameAction()) return;

    try {
      const response = await discard(roomId, playerId, selectedCardId[0], ownCards, selectedCardId[1]);
      if (response) {
        setRoomData(response);
        setSelectedCardId([]);
        setShowConfirmDeclare(false);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err.status === 400) {
        setErrorMessage(err?.response?.data?.message)
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }
  };

  const handleDeclareClick = async () => {
    if (!SignalRService.isReady() || turnExpired || actionInFlightRef.current) return;
    if (gameState !== GAME_STATE.playing || (playerInfo?.playerState !== PLAYER_STATE.pickedDeckCard && playerInfo?.playerState !== PLAYER_STATE.pickedDiscardedCard)) return;
    const toDiscardCard = ownCards?.find(c => c.cardId === selectedCardId[0]);
    if (!toDiscardCard || !selectedCardId || selectedCardId.length !== 1) {
      setErrorMessage(errorsAndWarnsMsg.selectCardToDeclare)
      return;
    }
    if (!beginGameAction()) return;

    try {
      const response = await declareWinner(roomId, playerId, selectedCardId[0]);
      if (response) {
        setRoomData(response);
        setSelectedCardId([]);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err.status === 400) {
        setErrorMessage(err?.response?.data?.message)
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }
  };

  const handleShowClick = async () => {
    if (!SignalRService.isReady() || actionInFlightRef.current) return;
    if (gameState !== GAME_STATE.winDeclared || playerInfo?.playerState !== PLAYER_STATE.declaredWin || !beginGameAction()) return;

    try {
      const response = await showWinningCard(roomId, playerId, ownCards);
      if (response) {
        setRoomData(response);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err.status === 400) {
        setErrorMessage(err?.response?.data?.message)
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }
  };

  const handleInviteClick = () => {
    if (!roomData || isRandomRoom) return;
    const gamePath = roomData?.gameKey === 'shan9mee' ? 'shan9mee' : 'poker';
    const appUrl = `${(APP_BASE_DOMAIN || window.location.origin).replace(/\/+$/, '')}/${gamePath}/${roomId}`;
    const shareData = {
      title: t('gameRoom.inviteTitle'),
      text: t('gameRoom.inviteText'),
      url: appUrl,
    };

    if (navigator.share) {
      // Use Web Share API
      navigator
        .share(shareData)
        .then(() => console.log("Successfully shared"))
        .catch((error) => console.error("Error sharing:", error));
    } else {
      // Fallback logic (e.g., copy to clipboard or show URL)
      navigator.clipboard.writeText(appUrl).then(() => {
        alert(t('gameRoom.urlCopied'));
      });
    }
  };

  const handleStartGame = async () => {
    if (!SignalRService.isReady() || actionInFlightRef.current || gameState !== GAME_STATE.initialHosted) return;
    if (playingPlayers.length < 2) {
      setErrorMessage(errorsAndWarnsMsg.invitePlayers);
      return;
    }
    if (!beginGameAction()) return;

    try {
      const response = await startGame(roomId, playerId);
      if (response) {
        setRoomData(response);
        resetPollingCount();
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err.status === 400) {
        setErrorMessage(err?.response?.data?.message)
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }

  }

  const handleReadyClick = async () => {
    if (!SignalRService.isReady() || actionInFlightRef.current || gameState !== GAME_STATE.readyForNextMatch) return;

    if (playingPlayers.length < 2) {
      setErrorMessage(errorsAndWarnsMsg.invitePlayers);
      return;
    }

    if (!beginGameAction()) return;

    try {
      setSelectedCardId([]);
      const response = await readyGame(roomId, playerId);
      if (response) {
        setRoomData(response);
        resetPollingCount();
        handlePlayAudio(gameAudios.readyClickSound)
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      if (err.status === 400) {
        setErrorMessage(err?.response?.data?.message)
      }
      else {
        setErrorMessage(errorsAndWarnsMsg.noInternet);
      }
    } finally {
      endGameAction();
    }
  }

  const isMoneyUpperAndLowerCard = (card) => {
    return roomData?.showedMoneyCardIds?.includes(card?.cardId) || false;
  }

  const isMoneyCard = (card) => {
    return roomData?.moneyCardIds?.includes(card?.cardId) || false;
  }

  const { handleLeaveRoom, handleLogoutFromRoom } = useRoomExit({
    roomId, playerId, setLoading, setErrorMessage, setConfirmText,
    onRoomLeft: resetPollingCount, fallbackError: errorsAndWarnsMsg.noInternet,
    lobbyPath: roomData?.gameKey === 'shan9mee' ? '/shan9mee' : '/poker',
  });

  const onRemoveClick = (player) => {
    setRemovePlayer(player);
    setShowRemoveDialog(true);
  }

  const handleRemoveClick = useRoomPlayerRemoval({
    roomId, playerId, isHost, targetPlayerId: removePlayer?.playerId,
    setLoading, setShowRemoveDialog, setErrorMessage,
    onRemoved: resetPollingCount, fallbackError: errorsAndWarnsMsg.noInternet,
  });

  const handleAutoArrange = async () => {
    setShowAutoArrangeConfirm(false);
    if (loading || !SignalRService.isReady() || turnExpired || !ownCards?.length) return;
    try {
      setLoading(true);
      const arranged = autoArrangeCards(ownCards);
      const response = await groupCards(roomId, playerId, arranged);
      if (!response) { setErrorMessage(errorsAndWarnsMsg.roomNotFound); return; }
      setRoomData(response);
      setOwnCards(arranged);
      setSelectedCardId([]);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || errorsAndWarnsMsg.noInternet);
    } finally { setLoading(false); }
  };

  const handleGroupClick = async () => {
    if (!SignalRService.isReady()) return;
    try {
      setLoading(true);

      if (!selectedCardId || selectedCardId.length <= 1) {
        setErrorMessage(errorsAndWarnsMsg.selectMoreThanOneCardsToGroup);
        return;
      }

      // Step 1: Group cards
      const lastGroupNum = Math.max(...ownCards.map(card => card.group));
      const updatedCards = ownCards.map(card => ({
        ...card,
        group: selectedCardId.includes(card.cardId) ? lastGroupNum + 1 : card.group,
      }));

      // Step 2: Sort cards
      const sortedCards = updatedCards.sort((a, b) => {
        if (b.group !== a.group) return b.group - a.group; // Primary sort by group descending

        // Secondary sort by selectedCardId order
        const indexA = selectedCardId.indexOf(a.cardId);
        const indexB = selectedCardId.indexOf(b.cardId);

        if (indexA !== -1 && indexB !== -1) return indexA - indexB; // Both in selectedCardId
        if (indexA !== -1 || indexB !== -1) return indexA !== -1 ? -1 : 1; // One in selectedCardId

        // Both not in selectedCardId, preserve original group order
        return ownCards.findIndex(card => card.cardId === a.cardId) -
          ownCards.findIndex(card => card.cardId === b.cardId);
      });

      // Step 3: Adjust single-card groups to group 0
      const groupCounts = sortedCards.reduce((acc, card) => {
        acc[card.group] = (acc[card.group] || 0) + 1;
        return acc;
      }, {});
      const normalizedCards = sortedCards.map(card => ({
        ...card,
        group: groupCounts[card.group] === 1 ? 0 : card.group,
      }));

      // Step 4: Re-map group values to sequential order
      const uniqueGroups = [...new Set(normalizedCards.map(card => card.group))].sort((a, b) => b - a);
      const groupMapping = uniqueGroups.reduce((acc, group, index) => {
        acc[group] = uniqueGroups.length - 1 - index;
        return acc;
      }, {});
      const rearrangedCards = normalizedCards.map(card => ({
        ...card,
        group: groupMapping[card.group],
      }));

      // Step 5: Submit grouped cards
      const response = await groupCards(roomId, playerId, rearrangedCards);
      if (response) {
        setOwnCards(rearrangedCards);
        setSelectedCardId([]);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.status === 400
        ? err?.response?.data?.message
        : errorsAndWarnsMsg.noInternet
      );
    } finally {
      setLoading(false);
    }
  };


  const isGroupOfLastCard = (card) => {
    const groupCards = ownCards?.filter(c => c.group === card.group);
    if (groupCards && groupCards.length === 1) {
      return true;
    }
    const indexOfTargetCard = groupCards?.findIndex(c => c.cardId === card.cardId);
    if (indexOfTargetCard < 0) {
      return false;
    }
    const isLast = indexOfTargetCard === groupCards?.length - 1;
    return isLast;
  }

  useEffect(() => {
    const sync = () => setIsFullScreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement || document.documentElement.classList.contains('pwf-pseudo-fullscreen')));
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      document.body.classList.remove('pwf-pseudo-fullscreen');
      document.documentElement.classList.remove('pwf-pseudo-fullscreen');
    };
  }, []);

  const enterFullscreen = async () => {
    // Fullscreen the document root so MUI menus/dialogs rendered in body
    // portals remain visible and interactive.
    const element = document.documentElement;
    const isiPhoneOrIPad = /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const enterPseudoFullscreen = () => {
      document.documentElement.classList.add('pwf-pseudo-fullscreen');
      document.body.classList.add('pwf-pseudo-fullscreen');
      setIsFullScreen(true);
      window.scrollTo(0, 0);
    };
    // iPhone Safari does not provide element fullscreen. Its exposed WebKit
    // methods are video-oriented, so use the app-sized fallback directly.
    if (isiPhoneOrIPad && !window.navigator.standalone) {
      enterPseudoFullscreen();
      return;
    }
    const request = element.requestFullscreen || element.webkitRequestFullscreen || element.mozRequestFullScreen || element.msRequestFullscreen;
    if (request) {
      try {
        await Promise.resolve(request.call(element));
        setIsFullScreen(true);
        return;
      } catch { /* iOS Safari may expose but reject the API. */ }
    }
    enterPseudoFullscreen();
  };

  const exitFullscreen = async () => {
    document.body.classList.remove('pwf-pseudo-fullscreen');
    document.documentElement.classList.remove('pwf-pseudo-fullscreen');
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
    if ((document.fullscreenElement || document.webkitFullscreenElement) && exit) {
      try { await Promise.resolve(exit.call(document)); } catch { /* State is synchronized below. */ }
    }
    setIsFullScreen(false);
  };

  const toggleFullscreen = () => isFullScreen ? exitFullscreen() : enterFullscreen();

  const handleSetMessage = (msg) => {
    setMessage(msg);
  }

  const handleSendMessage = async () => {
    if (isNullOrEmptyString(message?.trim())) {
      return;
    }
    try {
      setLoading(true);
      const response = await sendMessage(roomId, playerId, JSON.stringify(String(message)));
      if (response) {
        // The unversioned send response may predate a newer hub snapshot.
        // Read through the same revision guard as every other room update.
        await fetchRoomData();
        setMessage('');
        setNewMessageCount(0);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.status === 400
        ? err?.response?.data?.message
        : errorsAndWarnsMsg.noInternet
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setNewMessageCount(chatNotifications.current.observe(chatInfos, showChatDialog, playerId));
  }, [chatInfos, showChatDialog, playerId]);

  const toggleSound = () => {
    if (!soundAccess.allowed) { if (soundAccess.locked) setSubscribeOpen(true); return; }
    hasUserInteracted.current = true;
    audioRef.current?.unlock()?.catch(() => { });
    setIsSoundOn(prev => !prev);
  }

  const handleSaveSettings = async () => {
    if (!isHost || (gameState !== GAME_STATE.initialHosted && gameState !== GAME_STATE.readyForNextMatch)) {
      setErrorMessage(errorsAndWarnsMsg.cannotChangeSetting);
      setShowSettingDialog(false);
      return;
    }
    if (settingDeckCount === roomData?.deckCount && settingJokerPairs === roomData?.jokerPairs && settingCardsPerPlayer === (roomData?.cardsPerPlayer || 13) &&
        settingMaxWaitSeconds === (roomData?.maxWaitSeconds ?? null) && settingCardBackDesign === (roomData?.cardBackDesign || DEFAULT_CARD_BACK_DESIGN) &&
        JSON.stringify(settingMoneyCards) === JSON.stringify(roomData?.moneyCardSettings)) {
      setShowSettingDialog(false);
      setShowGameInfoDialog(true);
      return;
    }
    try {
      setLoading(true);
      const response = await changeSetting(roomId, playerId, settingDeckCount, settingJokerPairs,
        { cardsPerPlayer: settingCardsPerPlayer, moneyCards: settingMoneyCards, maxWaitSeconds: settingMaxWaitSeconds, cardBackDesign: settingCardBackDesign });
      if (response) {
        setRoomData(response);
        setShowGameInfoDialog(true);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.status === 400
        ? err?.response?.data?.message
        : errorsAndWarnsMsg.noInternet
      );
    } finally {
      setShowSettingDialog(false);
      setLoading(false);
    }
  }

  const canEditRoomSettings = isHost && [GAME_STATE.initialHosted, GAME_STATE.readyForNextMatch].includes(gameState);
  const handleOpenSettingDialog = () => {
    setSettingDeckCount(roomData?.deckCount);
    setSettingJokerPairs(roomData?.jokerPairs);
    setSettingCardsPerPlayer(roomData?.cardsPerPlayer || 13);
    setSettingMoneyCards({ ...DEFAULT_MONEY_CARDS, ...roomData?.moneyCardSettings });
    setSettingMaxWaitSeconds(roomData?.maxWaitSeconds ?? null);
    setSettingCardBackDesign(roomData?.cardBackDesign || DEFAULT_CARD_BACK_DESIGN);
    setShowSettingDialog(true);
  }

  const handleCancelSettings = () => {
    setShowSettingDialog(false);
  }

  const handleChangePlayerName = async () => {
    const newName = tempPlayerName?.trim();
    const oldName = playerInfo?.playerName?.trim();
    if (isNullOrEmptyString(newName) || newName?.length > maxPlayerNameLength) {
      setTempPlayerName(newName);
      return;
    }
    if (newName === oldName) {
      setTempPlayerName("");
      setShowPlayerNameDialog(false);
    } else {
      const isNameAlreadyExist = !!(allPlayers?.find(p => p.playerName?.trim() === newName));
      if (isNameAlreadyExist) {
        setTempPlayerName("");
        setShowPlayerNameDialog(false);
        setErrorMessage(errorsAndWarnsMsg.nameAlreadyExist)
      } else {
        try {
          setLoading(true);
          const response = await changeName(roomId, playerId, newName);
          if (response) {
            setRoomData(response);
            customStorage.setItem(STO_PLAYER_NAME, newName);
          } else {
            setErrorMessage(errorsAndWarnsMsg.roomNotFound);
          }
        } catch (err) {
          setErrorMessage(err?.status === 400
            ? err?.response?.data?.message
            : errorsAndWarnsMsg.noInternet
          );
        } finally {
          setLoading(false);
          setTempPlayerName("");
          setShowPlayerNameDialog(false);
        }
      }
    }
  }

  const handleRequestSwap = async (targetPlayerId) => {
    if (!canSwapPositions) return;
    try {
      setLoading(true);
      const response = await requestSwap(roomId, playerId, targetPlayerId);
      if (response) {
        setRoomData(response);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.status === 400
        ? err?.response?.data?.message
        : errorsAndWarnsMsg.noInternet
      );
    } finally {
      setLoading(false);
    }
  }

  const handleResponseSwap = async (requestPlayerId, isAccept = false) => {
    if (!canSwapPositions) {
      setErrorMessage(errorsAndWarnsMsg.cannotSwapWilePlaying);
      setShowSwapPositionsDialog(false);
      return;
    }

    try {
      setLoading(true);
      const response = await responseSwap(roomId, playerId, requestPlayerId, isAccept);
      if (response) {
        setRoomData(response);
      } else {
        setErrorMessage(errorsAndWarnsMsg.roomNotFound);
      }
    } catch (err) {
      setErrorMessage(err?.status === 400
        ? err?.response?.data?.message
        : errorsAndWarnsMsg.noInternet
      );
    } finally {
      setLoading(false);
    }
  }


  const isHandVisible =
    (gameState === GAME_STATE.playing || gameState === GAME_STATE.winDeclared) &&
    playerInfo?.playerState !== PLAYER_STATE.spectating && !isOpeningSequence;
  const gameMessageText = gameMessage?.key ? t(gameMessage.key, gameMessage.params) : gameMessage;

  useLayoutEffect(() => {
    const table = containerRef.current?.querySelector('.poker-deck');
    const deckZone = table?.querySelector('.desk-deck-zone');
    if (!table || !deckZone) return;

    // The middle grid row can shift when top/bottom players have different
    // card counts. Use the actual deck center, not 50% of the whole table.
    const alignDeskCards = () => {
      const tableBounds = table.getBoundingClientRect();
      const deckBounds = deckZone.getBoundingClientRect();
      const center = `${deckBounds.top + deckBounds.height / 2 - tableBounds.top}px`;
      if (table.style.getPropertyValue('--desk-discard-top') !== center) {
        table.style.setProperty('--desk-discard-top', center);
      }
    };

    alignDeskCards();
    const observer = new ResizeObserver(alignDeskCards);
    observer.observe(table);
    observer.observe(deckZone);
    table.querySelectorAll('.opponent, .desk-card-zone').forEach(element => observer.observe(element));
    window.addEventListener('resize', alignDeskCards);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', alignDeskCards);
    };
  }, [isFirstLoad, isHandVisible, gameState, allPlayers, lowerShowedCard]);

  return isFirstLoad || !cardBackReady ?
    (<CardShuffleLoading />)
    :
    (
      <div ref={containerRef} className="game-room-page" style={{ '--card-back-image': `url("${getCardBackImage(roomData?.cardBackDesign) || ''}")` }}>
        {/* <AppMetaData /> */}
        <LowInternet isNotOnline={!isOnline} />
        <LoadingSpinner show={loading} />
        <ChatDialog open={showChatDialog} onClose={() => setShowChatDialog(false)} chatInfos={chatInfos} playerInfos={playersForChat} playerId={playerId} roomId={roomId} message={message} typeMessage={handleSetMessage} onSendClick={handleSendMessage} />
        <ConfirmDialog open={showAutoArrangeConfirm} onCancel={() => setShowAutoArrangeConfirm(false)} onHide={() => setShowAutoArrangeConfirm(false)}
          title={t('gameRoom.autoArrange')} confirmText={t('gameRoom.autoArrangeConfirm')} onConfirm={handleAutoArrange} fullWidth />
        <HostJoinRequests roomId={roomId} requests={isHost && !isRandomRoom ? roomData?.pendingJoinRequests : []} onChanged={fetchRoomData} />
        <RoomInfoDialog open={showGameInfoDialog} onClose={() => setShowGameInfoDialog(false)} roomId={roomId} isRandomRoom={isRandomRoom} deckCount={roomData?.deckCount}
          jokerPair={roomData?.jokerPairs} players={allPlayers} hostId={hostPlayer?.playerId} isHost={isHost} onRemoveClick={onRemoveClick} />
        <WarningDialog open={!!errorMessage} fullWidth={true} title={t('gameRoom.gameInformation')} warningText={errorMessage}
          onClose={() => setErrorMessage('')} onHide={() => setErrorMessage('')} />
        <ConfirmDialog open={!!confirmText} onCancel={() => setConfirmText('')} onHide={() => setConfirmText('')} onConfirm={handleLeaveRoom} fullWidth={true} title={t('gameRoom.actionConfirmation')} confirmText={confirmText} />
        <ConfirmDialog open={showRemoveDialog} onCancel={() => setShowRemoveDialog(false)} onHide={() => setShowRemoveDialog(false)} onConfirm={handleRemoveClick} fullWidth={true} title={t('gameRoom.removeConfirmation')} confirmText={t('gameRoom.removeConfirmText', { playerName: removePlayer?.playerName })} />
        <ConfirmDialog open={showPickPairCardConfirm} onCancel={() => setShowPickPairCardConfirm(false)} onConfirm={() => executeDiscardedCardPick(true)} fullWidth={true} title={t('gameRoom.pickPairCardConfirmation')} confirmText={t('gameRoom.pickPairCardConfirmText')} />
        <ConfirmDialog open={showConfirmDeclare} onCancel={() => setShowConfirmDeclare(false)} onConfirm={() => executeDiscardedCardPick(true)} fullWidth={true} title={t('gameRoom.pickDiscardedCardConfirmation')} confirmText={t('gameRoom.pickDiscardedCardConfirmText')} />
        <ConfirmDialog open={!!jokerDiscardCardId} onCancel={() => setJokerDiscardCardId(null)} onHide={() => setJokerDiscardCardId(null)}
          title={t('gameRoom.jokerDiscardConfirmation')} confirmText={t('gameRoom.jokerDiscardConfirmText')} fullWidth
          onConfirm={() => {
            const cardId = jokerDiscardCardId;
            setJokerDiscardCardId(null);
            if (selectedCardId.length === 1 && selectedCardId[0] === cardId && ownCards.find(card => card.cardId === cardId)?.type === 'jokers') {
              handleDeclareClick();
            }
          }} />
        <AppMenuBar isRandomRoom={isRandomRoom} hasRoomMembership={!!playerInfo?.isJoined && playerInfo?.playerId === playerId} isHost={isHost} onSupportClick={() => setShowSupportDialog(true)} onLeaveRoomClick={() => setConfirmText(t('gameRoom.leaveRoomConfirm'))} onLogoutClick={handleLogoutFromRoom} roomId={roomId} newMessageCount={newMessageCount}
          isFullScreen={isFullScreen} toggleFullScreen={toggleFullscreen} onInviteClick={isRandomRoom ? undefined : handleInviteClick}
          onSettingClick={handleOpenSettingDialog}
          onSwapPositionsClick={canSwapPositions ? () => setShowSwapPositionsDialog(true) : undefined} onGameInfoClick={() => setShowGameInfoDialog(true)} onChatClick={() => { setShowChatDialog(true); setNewMessageCount(0); }}
          leadingTopBarControls={
              soundAccess.show && <IconButton
                size="large"
                aria-label={t('menuBar.gameSound')}
                title={t(soundAccess.locked ? 'subscription.required' : 'menuBar.gameSound')}
                aria-pressed={isSoundOn && soundAccess.allowed}
                disabled={!soundAccess.enabled}
                color="inherit"
                className="top-bar-control btn-sound"
                onClick={toggleSound}
              >
                {isSoundOn && soundAccess.allowed ? <MusicNoteIcon /> : <MusicOffIcon />}
              </IconButton>
          }
          topBarControls={roomId && playerId && <RoomVoiceChat roomId={roomId} playerId={playerId}
            hasOtherPlayer={(roomData?.players || []).filter(player => player.isJoined !== false &&
              ![PLAYER_STATE.leftRoom, PLAYER_STATE.dropped].includes(player.playerState)).length > 1} />}
        />
        <main
          className={`game-room-content${isHandVisible ? ' has-player-hand' : ''}`}
          data-game-state={gameState}
        >
          <SubscriptionWarning />
          <SubscriptionDialog open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />
          <RoomConnectionStatus state={connectionState} onRetry={() => SignalRService.retry()} />
          {gameState === GAME_STATE.readyForNextMatch && playerInfo.playerState === PLAYER_STATE.notReady && (
            <section className="next-match-panel">
              <span className="next-match-title">{t('gameRoom.playersForNextMatch')}</span>
              <div className="next-match-players">
                {allPlayers?.slice()?.sort((a, b) => {
                  if (a.playerState < b.playerState) return -1; // Ascending order
                  if (a.playerState > b.playerState) return 1;
                  return 0;
                })?.map((p, index) => (
                  <span
                    key={p.playerId}
                    className={`next-match-player${p.playerState === PLAYER_STATE.ready ? ' is-ready' : ''}${p.playerId === playerInfo?.playerId ? ' is-current' : ''}`}
                    onClick={() => {
                      if (p.playerId === playerInfo?.playerId) {
                        setShowPlayerNameDialog(true);
                      }
                    }}
                  >
                    {p.playerName}
                    {/* ({p.playerState === PLAYER_STATE.ready ? 'Ready' : 'Not Ready'}) */}
                  </span>
                ))}
              </div>
            </section>
          )}

          {(roomData && allPlayers && allPlayers.length > 0 && (gameState !== GAME_STATE.readyForNextMatch || playerInfo?.playerState === PLAYER_STATE.ready)) &&
            <section className="deck-section" data-player-count={allPlayers.length}>
              <div className="poker-deck-wrapper" data-player-count={allPlayers.length}>
              <div className={`poker-deck${lowerShowedCard && !isOpeningSequence ? ' has-lower-showed-card' : ''}`}>
                  {allPlayers?.map((player, index) => {
                    const playerTurn = isPlayerTurn(player);

                    const position = getAllPositionedPlayers(allPlayers?.length)[index];
                    const isNotPlaying = isSpectating(player?.playerState);
                    return (
                      <div
                        key={`${player.playerId}`}
                        className={`opponent seat-${position.slot}${player.playerId === playerInfo?.playerId ? ' is-current-player' : ''}${player.playerId === hostPlayer?.playerId ? ' is-host' : ''}${playerTurn ? ' is-active-player' : ''}${isNotPlaying ? ' is-inactive-player' : ''}`}
                      >
                        {(gameState === GAME_STATE.playing || gameState === GAME_STATE.winDeclared || gameState === GAME_STATE.initialHosted) &&
                          player.pickedCards?.length > 0 && <div className="picked-card-wrapper" onWheel={handleHorizontalCardScroll}>
                            {renderPickedCard(player.pickedCards?.slice()?.reverse())}
                          </div>}
                        <div className="player-panel">
                          <PlayerAvatar roomId={roomId} playerId={player.playerId} name={player.playerName}
                            className={`avatar-player${playerTurn ? ' active' : ''}${isNotPlaying ? ' leave' : ''}`}
                            sx={{
                              cursor: `${player.playerId === playerInfo?.playerId ? 'pointer' : 'default'}`
                            }}
                            onClick={() => {
                              if (player.playerId === playerInfo?.playerId) {
                                setShowPlayerNameDialog(true);
                              }
                            }}
                          />
                          <div className="player-summary">
                            <div className="player-name"><ExpandableText className="player-name-text" text={player.playerName} />
                              {playerTurn && turnSeconds !== null && <span className="turn-countdown" aria-label={t('gameSettings.timeRemaining', { count: turnSeconds })}>
                                {turnSeconds}s
                              </span>}
                            </div>
                            <div className="player-badges">
                              {player.playerId === hostPlayer?.playerId && <span className="player-badge host-badge">{t('gameRoom.host')}</span>}
                              {player.playerId === playerInfo?.playerId && <span className="player-badge self-badge">{t('gameRoom.you')}</span>}
                              {playerTurn && <span className="player-badge turn-badge">{t('gameRoom.turn')}</span>}
                              {player.isPreviousWinner && <span className="player-badge winner-badge">{t('gameRoom.winner')}</span>}
                            </div>
                            {isNotPlaying &&
                              <div className="player-state">{player.playerState === PLAYER_STATE.spectating ? t('gameRoom.guest') : t('gameRoom.dropped')}</div>}
                            {player.isJoined === false && <ExpandableText className="player-state auto-play-status" text={t('roomMembership.autoPlay')} />}
                            {(!isNotPlaying && gameState === GAME_STATE.readyForNextMatch) &&
                              <div className="player-state">{player.playerState === PLAYER_STATE.ready ? t('gameRoom.ready') : t('gameRoom.notReady')}</div>}
                          </div>
                        </div>

                        {(gameState === GAME_STATE.playing || gameState === GAME_STATE.winDeclared || gameState === GAME_STATE.initialHosted) ?
                          <>
                            <div className="player-discarded-cards" onWheel={handleHorizontalCardScroll}>
                              <div className="pokerCard-list">
                                {(
                                  (() => {
                                    const combinedPickedCards = allPlayers.flatMap(player => player.pickedCards);
                                    const pickedCardIds = new Set(combinedPickedCards?.map(pc => pc.cardId));
                                    const filteredCards = player?.discardedCards?.filter(dc =>
                                      (currentDiscardedCard === null || dc.cardId !== currentDiscardedCard.cardId) && !pickedCardIds.has(dc.cardId));
                                    if (filteredCards?.length > 0) {
                                      return filteredCards?.slice()?.reverse()?.map((card, ind) => (
                                        <div key={`${card?.cardId}`} className="pokerCard-item">
                                          <HalfCard card={card} isShowMoneySymbol={isMoneyUpperAndLowerCard(card)} />
                                        </div>
                                      ));
                                    } else {
                                      return <HalfCard card={{}} cardStyle={{ opacity: '0' }} />;
                                    }
                                  })()
                                )}
                              </div>
                            </div>
                          </> :
                          <div className="player-discarded-cards">
                            <div className="pokerCard-list">
                              <HalfCard card={{}} cardStyle={{ opacity: '0' }} />
                            </div>
                          </div>
                        }
                      </div>
                    )
                  }
                  )}

                  <div className="desk-card-zone" aria-label="Game desk cards">
                    <div className="desk-open-zone" aria-hidden="true" />
                    <div className="desk-discard-zone">
                      <div className="desk-discard-placeholder" aria-hidden="true" />
                    </div>
                    <div className="desk-deck-zone">
                      {(gameState === GAME_STATE.initialHosted || GAME_STATE === GAME_STATE.readyForNextMatch || deckCardCount > 0) &&
                        <div className={`card deck-card ${playerInfo?.playerState === PLAYER_STATE.turnPick ? 'deck-pickable-flash' : ''}`}
                          aria-disabled={!connectionReady || isOpeningSequence} onClick={connectionReady && !isOpeningSequence ? handlePickDeckCard : undefined}>
                          {(gameState === GAME_STATE.playing && deckCardCount > 0 && deckCardCount <= 10) &&
                            <span className="deck-count">{deckCardCount}</span>
                          }
                        </div>
                      }
                      {isOpeningShuffle && <div className="desk-shuffle-animation" role="status" aria-label={t('gameRoom.shufflingCards')}>
                        <span className="shuffle-card-back" /><span className="shuffle-card-back" /><span className="shuffle-card-back" />
                      </div>}
                      {!isOpeningSequence && lowerShowedCard && <div className="lower-showed-card-clip">
                        <div className="card lowest-show-card">
                          <PokerCard card={lowerShowedCard} isShowType={true} />
                        </div>
                      </div>}
                    </div>
                  </div>

                  {isOpeningDeal && <div className="opening-deal-animation" role="status" aria-label={t('gameRoom.dealingCards')}>
                    {allPlayers.flatMap((player, index) => {
                      if (isSpectating(player.playerState)) return [];
                      const position = getAllPositionedPlayers(allPlayers.length)[index];
                      return Array.from({ length: roomData?.cardsPerPlayer || 13 }, (_, round) => <span key={`${player.playerId}-${round}`} className="deal-card-back" style={{
                        '--deal-target-top': position?.top,
                        '--deal-target-left': position?.left,
                        '--deal-delay': `${round * 0.115 + index * 0.025}s`,
                        '--deal-rotation': `${(index - (allPlayers.length - 1) / 2) * 4}deg`,
                      }} />);
                    })}
                  </div>}

                  {!isOpeningSequence && <DeskDiscardCards roomData={roomData} playerId={playerId}
                    positions={getAllPositionedPlayers(roomData?.players?.length || 0)}
                    connectionReady={connectionReady} onPick={handlePickDiscardedCard} />}
                  {isPickedDeskCardMove &&
                    <div className="desk-animation-layer"><div className={`card deck-card`}
                      style={{
                        animation: `moveDeskCard 2s forwards`,
                        '--origin-top': 'var(--desk-discard-top)',
                        '--origin-left': 'var(--desk-deck-left)',
                        '--target-top': `${getAllPositionedPlayers(allPlayers?.length)[activePlayerIndex]?.top}`,
                        '--target-left': `${getAllPositionedPlayers(allPlayers?.length)[activePlayerIndex]?.left}`,
                      }}
                    >
                    </div></div>}
                </div>
              </div>
            </section>
          }


          {roomData && allPlayers && allPlayers.length > 0 && gameState === GAME_STATE.readyForNextMatch && playerInfo?.playerState !== PLAYER_STATE.ready &&
            <div className="declared-cards-section">
              <ShowedCardsResultRow upper={roomData.upperShowedCard} lower={roomData.lowerShowedCard} />
              {orderMatchPlayers(roomData.lastMatchResults?.length ? roomData.lastMatchResults : allPlayers, playerId)?.map((player, index) => {
                if (!!player.declaredCards && player.declaredCards?.length === 0) {
                  return null;
                }
                return (
                  <div key={index} className="declared-cards-wrapper" style={{ border: '0.063rem solid white', margin: '0.625rem', padding: '0.313rem', backgroundColor: '#e94560' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>
                      {t('gameRoom.playerCards', { playerName: player?.playerName })} {player?.isPreviousWinner ? t('gameRoom.winner') : ''}
                    </div>
                    <div className="declared-cards">
                      <div className="pokerCard-lists">
                        {player?.declaredCards?.map((card, ind) => {
                          return (
                            <div key={ind} className="pokerCard-item"
                            >
                              <HalfCard
                                card={card}
                                isShowPickedSymbol={player?.pickedCards?.find(c => c.cardId === card.cardId)}
                              // isShowMoneySymbol={isMoneyUpperAndLowerCard(card)}
                              />
                            </div>
                          )
                        })}

                      </div>
                    </div>
                    {[...player?.discardedCards || [], ...player?.declaredCards || []]?.filter(c => isMoneyCard(c))?.length > 0 &&
                      <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'gold', fontWeight: 'bold', fontSize: '0.7rem' }}>
                          {t('gameRoom.moneyCards')}
                        </div>
                        <div className="money-cards" style={{ marginTop: '0.313rem' }}>
                          <div className="pokerCard-lists">
                            {[...player?.discardedCards || [], ...player?.declaredCards || []]?.filter(c => isMoneyCard(c) && !player?.pickedCards?.find(pc => pc.cardId === c.cardId))?.map((card, ind) => {
                              return (
                                <div key={ind} className="pokerCard-item" style={{ marginRight: '0.125rem' }}
                                >
                                  <HalfCard
                                    card={card}
                                    isShowMoneySymbol={isMoneyUpperAndLowerCard(card)}
                                    isFullLast={true}
                                    isShowType={true}
                                  // isShowMoneySymbol={isMoneyUpperAndLowerCard(card)}
                                  />
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </>
                    }
                  </div>
                )
              })}
            </div>}


          {roomData && allPlayers && allPlayers.length > 0 && gameMessage &&
            <section className="game-action-section" style={{
              position: gameState === GAME_STATE.readyForNextMatch ? 'sticky' : '',
              bottom: gameState === GAME_STATE.readyForNextMatch ? '0' : ''
            }}>
              {gameMessage &&
                <div className="game-message">
                  <Typography className="msg-info"><ExpandableText text={gameMessageText} /></Typography>
                </div>
              }
              <fieldset className="game-action-buttons" disabled={!connectionReady || isOpeningSequence || (gameState === GAME_STATE.playing && turnExpired)} style={{ border: 0, padding: 0, margin: 0 }}>
                {(gameState === GAME_STATE.initialHosted && isHost) &&
                  <Button aria-label={t('gameRoom.start')} title={t('gameRoom.start')} startIcon={<PlayArrowIcon />} className="app-btn" variant="contained" color="primary" sx={{ textTransform: 'none' }} onClick={handleStartGame}>
                    <span className="action-label">{t('gameRoom.start')}</span>
                  </Button>
                }
                {gameState === GAME_STATE.readyForNextMatch && playerInfo?.playerState === PLAYER_STATE.notReady &&
                  <Button aria-label={t('gameRoom.readyBtn')} title={t('gameRoom.readyBtn')} startIcon={<CheckIcon />} className="app-btn" variant="contained" color="primary" sx={{ textTransform: 'none' }} onClick={handleReadyClick}>
                    <span className="action-label">{t('gameRoom.readyBtn')}</span>
                  </Button>
                }
                {!isRandomRoom && (gameState === GAME_STATE.initialHosted || gameState === GAME_STATE.readyForNextMatch) &&
                  <>
                    <Button aria-label={t('gameRoom.invite')} title={t('gameRoom.invite')} startIcon={<PersonAddIcon />} className="app-btn" variant="contained" color="success" sx={{ textTransform: 'none' }} onClick={handleInviteClick}>
                      <span className="action-label">{t('gameRoom.invite')}</span>
                    </Button>
                  </>
                }
                {(playerInfo?.playerState === PLAYER_STATE.pickedDeckCard || playerInfo?.playerState === PLAYER_STATE.pickedDiscardedCard) &&
                  <>
                    <DeclareWinButton onConfirm={handleDeclareClick} disabled={loading || !connectionReady || turnExpired} />
                    {playerInfo?.pickedCards?.length < 4 && !playerInfo?.mustDeclareWinner && !playerInfo?.mustDeclareWinner &&
                      <Button aria-label={t('gameRoom.discard')} title={t('gameRoom.discard')} startIcon={<DeleteOutlineIcon />} className="app-btn" variant="contained" color="success" sx={{ textTransform: 'none' }} onClick={handleDiscardClick}>
                        <span className="action-label">{t('gameRoom.discard')}</span>
                      </Button>
                    }
                  </>
                }
                {gameState === GAME_STATE.winDeclared && playerInfo?.playerState === PLAYER_STATE.declaredWin &&
                  <Button aria-label={t('gameRoom.showWinningCards')} title={t('gameRoom.showWinningCards')} startIcon={<VisibilityIcon />} className="app-btn" variant="contained" color="success" sx={{ textTransform: 'none' }} onClick={handleShowClick}>
                    <span className="action-label">{t('gameRoom.showWinningCards')}</span>
                  </Button>
                }
              </fieldset>
            </section>}

          {isHandVisible && (
            <div className="hand-wrapper">
              <div className="cards-in-hand">
                <div className="pokerCard-list">
                  {buildHandRows(groupedCards).map((row, rowIndex) => (
                    <div className="hand-row" key={rowIndex} style={{ '--hand-row-units': row.units, '--hand-row-gaps': `${Math.max(0, row.segments.length - 1) * 0.28}rem` }}>
                      {row.segments.map((group, idx) => (
                        <div key={idx} className="hand-group">
                          {group.map(({ card, index }, segmentIndex) => {
                            const isShowTypeAndFull = isGroupOfLastCard(card);
                            const isPickedCard = !!playerInfo?.pickedCards?.find(c => c.cardId === card.cardId);
                            return (
                              <DraggableCard
                                key={card?.cardId}
                                index={index}
                                group={card?.group}
                                card={card}
                                moveCard={moveCard}
                                selectedCardId={selectedCardId}
                                isShowPickedSymbol={isPickedCard}
                                onHandleClick={handleCardSelect}
                                isShowMoneySymbol={isMoneyUpperAndLowerCard(card) && !isPickedCard}
                                isShowType={isShowTypeAndFull}
                                isFullLast={isShowTypeAndFull || segmentIndex === group.length - 1}
                              />
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="hand-arrangement-controls">
                <Tooltip title={t('gameRoom.autoArrange')}>
                  <IconButton className="btn-group-card" aria-label={t('gameRoom.autoArrange')} disabled={loading || !connectionReady || turnExpired} onClick={() => setShowAutoArrangeConfirm(true)}><SortIcon /></IconButton>
                </Tooltip>
                <Tooltip title={t('gameRoom.groupCards')}>
                  <IconButton disabled={!connectionReady} color="primary" className="btn-group-card" aria-label={t('gameRoom.groupCards')} onClick={handleGroupClick}>
                    <ViewModuleIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          )
          }
          <CustomDragLayer />
        </main>
        {/* Dialog for Game Settings */}
        <Dialog fullWidth={true} open={showSettingDialog} PaperProps={{ component: 'form', onSubmit: event => {
          event.preventDefault();
          if (!loading && canEditRoomSettings) handleSaveSettings();
        } }}>
          <DialogTitle>
            {t(canEditRoomSettings ? 'gameRoom.changeRoomSettings' : 'gameSettings.viewSettings')}
            <IconButton
              aria-label="close"
              onClick={handleCancelSettings}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <GameSettingsFields deckCount={canEditRoomSettings ? settingDeckCount || 2 : roomData?.deckCount || 2}
              jokerPairs={canEditRoomSettings ? (settingJokerPairs === '' ? 2 : settingJokerPairs) : roomData?.jokerPairs ?? 0}
              cardsPerPlayer={canEditRoomSettings ? settingCardsPerPlayer : roomData?.cardsPerPlayer || 13}
              readOnly={!canEditRoomSettings}
              moneyCards={canEditRoomSettings ? settingMoneyCards : { ...DEFAULT_MONEY_CARDS, ...roomData?.moneyCardSettings }}
              maxWaitSeconds={canEditRoomSettings ? settingMaxWaitSeconds : roomData?.maxWaitSeconds ?? null}
              cardBackDesign={canEditRoomSettings ? settingCardBackDesign : roomData?.cardBackDesign || DEFAULT_CARD_BACK_DESIGN}
              onChange={settings => { setSettingDeckCount(settings.deckCount); setSettingJokerPairs(settings.jokerPairs);
                setSettingCardsPerPlayer(settings.cardsPerPlayer); setSettingMoneyCards(settings.moneyCards); setSettingMaxWaitSeconds(settings.maxWaitSeconds); setSettingCardBackDesign(settings.cardBackDesign); }} />
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <Button className="app-btn" variant="contained" color="secondary" onClick={handleCancelSettings}>{t('common.cancel')}</Button>
            {canEditRoomSettings && <Button type="submit" disabled={loading} className="app-btn" variant="contained" color="success">{t('common.change')}</Button>}
          </DialogActions>
        </Dialog>

        {/* Dialog for Support/Feedback */}
        <FeedbackDialog open={showSupportDialog} onClose={() => setShowSupportDialog(false)} roomId={roomId} />

        {/* Dialog for Player Name */}
        <Dialog fullWidth={true} open={showPlayerNameDialog}>
          <DialogTitle>
            {t('gameRoom.changeYourName')}
            <IconButton
              aria-label="close"
              onClick={() => { setTempPlayerName(''); setShowPlayerNameDialog(false); }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              autoComplete="off"
              margin="dense"
              label={t('gameRoom.playerName')}
              type="text"
              fullWidth
              variant="standard"
              value={tempPlayerName}
              onKeyDown={event => { if (event.key === 'Enter' && !event.nativeEvent.isComposing && !loading) { event.preventDefault(); handleChangePlayerName(); } }}
              onChange={(e) => setTempPlayerName(e.target.value)}
              error={isNullOrEmptyString(tempPlayerName) || tempPlayerName.length > maxPlayerNameLength}
              helperText={
                isNullOrEmptyString(tempPlayerName)
                  ? t('gameRoom.playerNameRequired')
                  : tempPlayerName.length > maxPlayerNameLength
                    ? t('gameRoom.playerNameMaxLength', { max: maxPlayerNameLength })
                    : ""
              }
            />
          </DialogContent>
          <DialogActions>
            <Button className="app-btn" variant="contained"
              onClick={async () => {
                if (isNullOrEmptyString(tempPlayerName) || tempPlayerName.length > maxPlayerNameLength) {
                  return;
                }
                await handleChangePlayerName();
              }}
            >
              {t('common.change')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Swap Positions */}
        <Dialog fullWidth={true} open={showSwapPositionsDialog && canSwapPositions}>
          <DialogTitle sx={{ m: 0, p: 2 }} id="swapPositions-dialog-title">
            {t('gameRoom.swapPositions')}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => { setShowSwapPositionsDialog(false) }}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <table className="table table-bordered border-stripe tbl-game-info">
              <>
                <thead>
                  <tr>
                    <td className='title' colSpan={2}>
                      {allPlayers && allPlayers?.filter(p => p.playerId !== playerId)?.length > 0 ? t('gameRoom.opponentPlayers') : t('gameRoom.noOpponentPlayer')}
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {allPlayers && allPlayers?.filter(p => p.playerId !== playerId)?.length > 0 &&
                    (
                      allPlayers?.filter(p => p.playerId !== playerId).map(player => {
                        const isRequested = !!(swapRequests?.find(sr => sr.targetPlayerId === player.playerId));
                        const isRespone = !!(swapRequests?.find(sr => sr.requestPlayerId === player.playerId));
                        return (
                          <tr key={player?.playerId}>
                            <td className="column-title">
                              {player.playerName}
                            </td>
                            {isRequested ?
                              (<td>
                                <button onClick={() => handleRequestSwap(player.playerId)} type='button' className='btn-remove'>{t('common.cancel')}</button>
                              </td>) : isRespone ? (
                                <td>
                                  <button onClick={() => handleResponseSwap(player.playerId, true)} type='button' className='btn-remove mb-1 me-1'>{t('common.accept')}</button>
                                  <button onClick={() => handleResponseSwap(player.playerId)} type='button' className='btn-remove'>{t('common.decline')}</button>
                                </td>
                              ) : (
                                <td>
                                  <button onClick={() => handleRequestSwap(player.playerId)} type='button' className='btn-remove'>{t('common.request')}</button>
                                </td>
                              )

                            }
                          </tr>
                        )
                      })
                    )
                  }
                </tbody>
              </>
            </table>

          </DialogContent>
        </Dialog>

      </div>
    );
};

export default function GameRoomRoute() {
  const { roomId, playerId } = useParams();
  return <GameRoom key={`${roomId}:${playerId}`} />;
}
